import api from '../utils/api';

/**
 * Invoice Service - Handles invoice generation and management
 */

export const invoiceService = {
  /**
   * Generate invoice for an order
   * @param {string} orderId - Order ID
   * @returns {Promise}
   */
  generateInvoice: async (orderId) => {
    try {
      const response = await api.post(`/orders/${orderId}/invoice`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to generate invoice');
    }
  },

  /**
   * Get invoice by invoice number
   * @param {string} invoiceNumber - Invoice number
   * @returns {Promise}
   */
  getInvoice: async (invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceNumber}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice');
    }
  },

  /**
   * Get invoice for an order
   * @param {string} orderId - Order ID
   * @returns {Promise}
   */
  getInvoiceByOrder: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}/invoice`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice');
    }
  },

  /**
   * Get all invoices (admin)
   * @param {Object} filters - Filters (status, startDate, endDate, customerId)
   * @returns {Promise}
   */
  getAllInvoices: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.customerId) params.append('customerId', filters.customerId);
      
      const response = await api.get(`/admin/invoices?${params.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoices');
    }
  },

  /**
   * Download invoice as PDF
   * @param {string} invoiceNumber - Invoice number
   * @returns {Promise}
   */
  downloadInvoicePDF: async (invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceNumber}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to download invoice');
    }
  },

  /**
   * Send invoice via email
   * @param {string} invoiceNumber - Invoice number
   * @param {string} email - Email address (optional, uses customer email if not provided)
   * @returns {Promise}
   */
  sendInvoiceEmail: async (invoiceNumber, email = null) => {
    try {
      const response = await api.post(`/invoices/${invoiceNumber}/send-email`, { email });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send invoice email');
    }
  },

  /**
   * Cancel/void invoice
   * @param {string} invoiceNumber - Invoice number
   * @param {string} reason - Cancellation reason
   * @returns {Promise}
   */
  cancelInvoice: async (invoiceNumber, reason) => {
    try {
      const response = await api.post(`/invoices/${invoiceNumber}/cancel`, { reason });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel invoice');
    }
  },

  /**
   * Get invoice statistics
   * @param {Object} filters - Filters (dateRange)
   * @returns {Promise}
   */
  getInvoiceStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      
      const response = await api.get(`/admin/invoices/stats?${params.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice statistics');
    }
  }
};

export default invoiceService;


